import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { serviceApiService } from "../services/serviceApiService";
import {
  type CreateServiceRequest,
  type ReorderServicesRequest,
  type Service,
  type UpdateServiceRequest,
  fromCreateServiceRequestToCreateServiceDto,
  fromReorderServicesRequestToReorderServicesDto,
  fromServiceDtoToService,
  fromUpdateServiceRequestToUpdateServiceDto,
} from "../types/serviceTypes";
import { serviceKeys, updateServiceCatalogue, upsertService } from "./serviceCache";
import { compareServiceOrder } from "../utils/serviceOrder";
import { appointmentFormKeys } from "@/features/appointments/hooks/appointmentFormQueries";

export { serviceKeys } from "./serviceCache";

function useServiceCatalogueOptions() {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();

  return queryOptions({
    queryKey: serviceKeys.catalogue(user?.id, businessId),
    queryFn: async ({ signal }) => {
      if (!user || !businessId) throw new Error("A user and business are required to load services.");
      const dtos = await serviceApiService.getAll(businessId, signal);
      return dtos.map(fromServiceDtoToService).sort(compareServiceOrder);
    },
    enabled: !!user && !!businessId,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export const useServices = () => useQuery(useServiceCatalogueOptions());

const selectActiveServices = (services: Service[]) => services.filter((service) => service.isActive);

export const useActiveServices = () => useQuery({
  ...useServiceCatalogueOptions(),
  select: selectActiveServices,
});

export const useService = (id?: string) => {
  const options = useServiceCatalogueOptions();
  return useQuery({
    ...options,
    enabled: options.enabled && !!id,
    select: (services) => services.find((service) => service.id === id),
  });
};

export const useCreateService = () => {
  const queryClient = useQueryClient();
  const { user, getAuthGeneration } = useAuth();

  return useMutation({
    mutationFn: async (data: CreateServiceRequest) => {
      const dto = fromCreateServiceRequestToCreateServiceDto(data);
      return fromServiceDtoToService(await serviceApiService.create(dto));
    },
    onMutate: (data) => ({ queryKey: serviceKeys.catalogue(user?.id, data.businessId), generation: getAuthGeneration() }),
    onSuccess: (service, _data, context) => {
      if (context.generation !== getAuthGeneration()) return;
      return Promise.all([
        updateServiceCatalogue(queryClient, context.queryKey, (services) => upsertService(services, service)),
        queryClient.invalidateQueries({ queryKey: appointmentFormKeys.scope(context.queryKey[1], context.queryKey[2]) }),
      ]);
    },
  });
};

export const useUpdateService = (id: string) => {
  const queryClient = useQueryClient();
  const { user, getAuthGeneration } = useAuth();
  const { businessId } = useCurrentBusiness();

  return useMutation({
    mutationFn: async (data: UpdateServiceRequest) => {
      const dto = fromUpdateServiceRequestToUpdateServiceDto(data);
      return fromServiceDtoToService(await serviceApiService.update(id, dto));
    },
    onMutate: () => ({ queryKey: serviceKeys.catalogue(user?.id, businessId), generation: getAuthGeneration() }),
    onSuccess: (service, _data, context) => {
      if (context.generation !== getAuthGeneration()) return;
      return Promise.all([
        updateServiceCatalogue(queryClient, context.queryKey, (services) => upsertService(services, service)),
        queryClient.invalidateQueries({ queryKey: appointmentFormKeys.scope(context.queryKey[1], context.queryKey[2]) }),
      ]);
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();

  return useMutation({
    mutationFn: (id: string) => serviceApiService.delete(id),
    onMutate: () => ({ queryKey: serviceKeys.catalogue(user?.id, businessId) }),
    onSuccess: (_result, id, context) =>
      updateServiceCatalogue(queryClient, context.queryKey, (services) =>
        services.filter((service) => service.id !== id)),
  });
};

export const useReorderServices = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: ReorderServicesRequest) => {
      const dto = fromReorderServicesRequestToReorderServicesDto(data);
      const resultDtos = await serviceApiService.reorder(data.businessId, dto);
      return resultDtos.map(fromServiceDtoToService).sort(compareServiceOrder);
    },
    onMutate: (data) => ({ queryKey: serviceKeys.catalogue(user?.id, data.businessId) }),
    onSuccess: (services, _data, context) =>
      updateServiceCatalogue(queryClient, context.queryKey, () => services),
  });
};
